import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Button,
  Paper,
  ButtonGroup,
  useTheme,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  PaymentOutlined as PaymentIcon,
  RemoveShoppingCart as ClearCartIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const CartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden'
}));

const CartHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CartItemsList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: 0,
}));

const CartItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const CartFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const TotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
}));

const CheckoutButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

/**
 * Shopping cart component for the POS system
 * @param {Object} props Component props
 * @param {Array} props.cartItems Items in the cart
 * @param {Function} props.onUpdateQuantity Callback for updating item quantity
 * @param {Function} props.onRemoveItem Callback for removing an item
 * @param {Function} props.onCheckout Callback for checkout
 * @param {Function} props.onClearCart Callback for clearing the entire cart
 * @param {Function} props.onUpdatePrice Callback for updating item price
 * @param {Object} props.totals Cart totals object (subtotal, taxAmount, discount, additionalCharges, total)
 * @param {number} props.discount Current discount amount
 * @param {Function} props.onDiscountChange Callback for discount change
 * @param {number} props.additionalCharges Current additional charges amount
 * @param {Function} props.onAdditionalChargesChange Callback for additional charges change
 * @returns {JSX.Element} Cart component
 */
const POSCart = ({ 
  cartItems, 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout, 
  onClearCart,
  onUpdatePrice,
  totals,
  discount,
  onDiscountChange,
  additionalCharges,
  onAdditionalChargesChange
}) => {
  const theme = useTheme();
  const [quantityInputs, setQuantityInputs] = useState({});
  const [priceInputs, setPriceInputs] = useState({});

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Handle quantity change from manual input
  const handleQuantityChange = (productId, value) => {
    // Store the raw input value in state first
    setQuantityInputs({
      ...quantityInputs,
      [productId]: value
    });
    
    // Convert to number and validate
    const quantity = parseInt(value, 10);
    if (!isNaN(quantity) && quantity >= 0) {
      onUpdateQuantity(productId, quantity);
    }
  };

  // Get displayed value for an item's quantity input
  const getQuantityInputValue = (item) => {
    // If the user is actively editing this field, show their current input
    if (quantityInputs[item.product.id] !== undefined) {
      return quantityInputs[item.product.id];
    }
    // Otherwise, show the actual quantity
    return item.quantity;
  };

  // Handle blur event to clean up invalid inputs
  const handleQuantityBlur = (productId) => {
    const currentInput = quantityInputs[productId];
    if (currentInput === '' || isNaN(parseInt(currentInput, 10))) {
      // Reset to the valid value from cart
      const item = cartItems.find(item => item.product.id === productId);
      if (item) {
        setQuantityInputs({
          ...quantityInputs,
          [productId]: item.quantity.toString()
        });
      }
    }
  };

  // Update quantity using buttons instead of manual input
  const handleButtonQuantityChange = (productId, newQuantity) => {
    // Update the cart
    onUpdateQuantity(productId, newQuantity);
    
    // Also update the input state to keep the display in sync
    setQuantityInputs({
      ...quantityInputs,
      [productId]: newQuantity.toString()
    });
  };

  // Handle price change from manual input
  const handlePriceChange = (productId, value, minPrice) => {
    setPriceInputs({
      ...priceInputs,
      [productId]: value
    });

    const price = parseFloat(value);
    if (!isNaN(price) && price >= minPrice) {
      onUpdatePrice(productId, price);
    }
  };

  // Handle blur event to reset invalid price inputs
  const handlePriceBlur = (productId, minPrice) => {
    const currentInput = priceInputs[productId];
    const num = parseFloat(currentInput);
    if (isNaN(num) || num < minPrice) {
      const item = cartItems.find(i => i.product.id === productId);
      if (item) {
        setPriceInputs({
          ...priceInputs,
          [productId]: item.price.toString()
        });
      }
    }
  };

  return (
    <CartContainer>
      <CartHeader>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CartIcon sx={{ mr: 1 }} />
            Shopping Cart
            <Typography 
              variant="body2" 
              component="span" 
              sx={{ ml: 1, bgcolor: 'primary.main', color: 'primary.contrastText', px: 1, py: 0.5, borderRadius: 1 }}
            >
              {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
            </Typography>
          </Typography>
          {cartItems.length > 0 && (
            <IconButton 
              onClick={onClearCart}
              color="error"
              size="small"
              title="Clear cart"
            >
              <ClearCartIcon />
            </IconButton>
          )}
        </Box>
      </CartHeader>
      
      <CartItemsList>
        {cartItems.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, p: 2 }}>
            <CartIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              Your cart is empty
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
              Add products to create a new sale
            </Typography>
          </Box>
        ) : (
          cartItems.map((item) => (
            <React.Fragment key={item.product.id}>
              <CartItem>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" noWrap title={item.product.name}>
                      {item.product.name}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {formatCurrency(item.price * item.quantity)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ButtonGroup size="small" variant="outlined">
                        <Button 
                          onClick={() => handleButtonQuantityChange(item.product.id, item.quantity - 1)}
                          size="small"
                          disabled={item.quantity <= 1}
                        >
                          <RemoveIcon fontSize="small" />
                        </Button>
                        <TextField
                          size="small"
                          value={getQuantityInputValue(item)}
                          onChange={(e) => handleQuantityChange(item.product.id, e.target.value)}
                          onBlur={() => handleQuantityBlur(item.product.id)}
                          inputProps={{ 
                            min: 1,
                            style: { textAlign: 'center', width: '30px', padding: '4px' }
                          }}
                          sx={{ 
                            width: '50px',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { border: 'none' },
                            }
                          }}
                        />
                        <Button 
                          onClick={() => handleButtonQuantityChange(item.product.id, item.quantity + 1)}
                          size="small"
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      </ButtonGroup>
                      
                      <Box sx={{ ml: 1 }}>
                        <Tooltip title={`Minimum price $${Number(item.product.minSalePrice || 0).toFixed(2)}`} arrow>
                          <TextField
                            size="small"
                            value={priceInputs[item.product.id] !== undefined ? priceInputs[item.product.id] : item.price}
                            onChange={(e) => handlePriceChange(item.product.id, e.target.value, item.product.minSalePrice || 0)}
                            onBlur={() => handlePriceBlur(item.product.id, item.product.minSalePrice || 0)}
                            inputProps={{
                              style: { textAlign: 'center', width: '70px', padding: '4px' },
                              step: 0.01,
                              min: item.product.minSalePrice || 0,
                              type: 'number'
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            error={priceInputs[item.product.id] !== undefined && parseFloat(priceInputs[item.product.id]) < (item.product.minSalePrice || 0)}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <IconButton 
                      edge="end" 
                      size="small"
                      onClick={() => onRemoveItem(item.product.id)}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CartItem>
              <Divider />
            </React.Fragment>
          ))
        )}
      </CartItemsList>
      
      <CartFooter>
        {/* Discount and Additional Charges Input - Compact Version */}
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 'bold', display: 'block' }}>
              Adjustments
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Discount"
                type="number"
                size="small"
                value={discount}
                onChange={(e) => onDiscountChange(e.target.value)}
                InputProps={{
                  startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-input': { fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
              <TextField
                label="Add. Charges"
                type="number"
                size="small"
                value={additionalCharges}
                onChange={(e) => onAdditionalChargesChange(e.target.value)}
                InputProps={{
                  startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ 
                  flex: 1,
                  '& .MuiInputBase-input': { fontSize: '0.875rem' },
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' }
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Totals Summary - Compact */}
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
            <TotalRow sx={{ mb: 0.5 }}>
              <Typography variant="caption">Subtotal:</Typography>
              <Typography variant="caption">{formatCurrency(totals.subtotal)}</Typography>
            </TotalRow>
            {totals.discount > 0 && (
              <TotalRow sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="error">Discount:</Typography>
                <Typography variant="caption" color="error">-{formatCurrency(totals.discount)}</Typography>
              </TotalRow>
            )}
            <TotalRow sx={{ mb: 0.5 }}>
              <Typography variant="caption">Tax:</Typography>
              <Typography variant="caption">{formatCurrency(totals.taxAmount)}</Typography>
            </TotalRow>
            {totals.additionalCharges > 0 && (
              <TotalRow sx={{ mb: 0.5 }}>
                <Typography variant="caption">Add. Charges:</Typography>
                <Typography variant="caption">{formatCurrency(totals.additionalCharges)}</Typography>
              </TotalRow>
            )}
            <Divider sx={{ my: 0.5 }} />
            <TotalRow>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totals.total)}
              </Typography>
            </TotalRow>
          </CardContent>
        </Card>
        
        <CheckoutButton
          variant="contained"
          color="primary"
          fullWidth
          size="medium"
          startIcon={<PaymentIcon />}
          onClick={onCheckout}
          disabled={cartItems.length === 0}
          sx={{ mt: 1 }}
        >
          Proceed to Payment
        </CheckoutButton>
      </CartFooter>
    </CartContainer>
  );
};

export default POSCart; 